import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import SyntaxAnalysisPage from "../../analysis/syntax_analysis/SyntaxAnalysisPage";
import { getContentHeight } from "../../../../../utils/textUtils";
import "./BookSyntaxPage.css";

// 리더스북 · 파닉스북 구문분석(2026-10-20 대표: "리더스 · 파닉스 교재처럼"). 고르기 · 채점 · 저장은 지문분석 구문분석
// 화면을 그대로 쓰고(재구축 금지), 겉모습만 ECHO 교재 쪽처럼 입힌다:
// 쪽 머리띠(Unit · 책 이름 · 활동) → 두 색 제목 → 동그라미 글자(A) 달린 활동 띠 → 문장 → 낱말 칩 같은 성분 단추(S · V · O 동그라미).
// 꾸밈은 BookSyntaxPage.css 가 .book-syntax 아래에서만 건다(지문분석 구문분석은 그대로).
const DEFAULT_ACCENT = "#D6336C";

const normalizeSentence = (text) => String(text || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const sentenceTextOf = (item) =>
  (item.tokens || []).map((token) => `${token.text}${token.space_right ? " " : ""}`).join("");

// 문장 하나를 한 번 들려준다. 파닉스북은 이야기 음원에서 그 문장 구간, 리더스북은 그 문장 mp3(item.audio),
// 없으면 브라우저 음성.
const playSentence = (item, audioRef) => {
  if (audioRef.current) audioRef.current.pause();
  window.speechSynthesis?.cancel();
  const clip = item.audio;
  if (clip?.url) {
    const audio = new Audio(clip.url);
    audioRef.current = audio;
    // 구간이 없으면(리더스북 문장 mp3) 처음부터 끝까지 튼다
    if (typeof clip.start_ms !== "number") {
      audio.play().catch(() => {});
      return;
    }
    const start = () => {
      audio.currentTime = clip.start_ms / 1000;
      audio.play().catch(() => {});
    };
    audio.addEventListener("loadedmetadata", start, { once: true });
    audio.addEventListener("timeupdate", () => {
      if (audio.currentTime * 1000 >= clip.end_ms) audio.pause();
    });
    audio.load();
    return;
  }
  if (!window.speechSynthesis || typeof SpeechSynthesisUtterance === "undefined") return;
  const utterance = new SpeechSynthesisUtterance(sentenceTextOf(item));
  utterance.lang = "en-US";
  utterance.rate = 0.9;
  window.speechSynthesis.speak(utterance);
};

const BookSyntaxPage = ({ setCurrentStep, isPreview, isReview }) => {
  const { assignment, section } = useSelector((state) => state.assignment);
  const book = section?.question?.book || {};
  const accent = book.theme_color || DEFAULT_ACCENT;
  // 과제 이름이 'Ginedu Phonics · Unit 01 · Tap, Tap, Tap!' 꼴이면 Unit 칸을 머리띠에 쓴다
  const unitLabel = String(assignment?.title || "").split(" · ").find((part) => /^(Unit|Starter)/.test(part)) || "";

  // 문장이 처음 뜰 때 한 번만 들려준다(2026-10-20 대표). 지금 문장은 구문분석 화면 안에만 있어서
  // 그 화면은 고치지 않고 문장 자리(.syntax-sentence)의 글이 바뀌는 것을 지켜본다.
  const bodyRef = useRef(null);
  const audioRef = useRef(null);
  useEffect(() => {
    const body = bodyRef.current;
    const itemList = section?.question?.data || [];
    if (!body || !itemList.length) return undefined;
    const itemByKey = new Map(itemList.map((item) => [normalizeSentence(sentenceTextOf(item)), item]));
    const playedKeySet = new Set();
    let shownKey = "";
    const check = () => {
      const key = normalizeSentence(body.querySelector(".syntax-sentence")?.textContent);
      if (!key || key === shownKey) return;
      shownKey = key;
      const item = itemByKey.get(key);
      if (!item || playedKeySet.has(key)) return;
      playedKeySet.add(key);
      playSentence(item, audioRef);
    };
    const observer = new MutationObserver(check);
    observer.observe(body, { childList: true, subtree: true, characterData: true });
    check();
    return () => {
      observer.disconnect();
      if (audioRef.current) audioRef.current.pause();
      window.speechSynthesis?.cancel();
    };
  }, [section?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="book-syntax" style={{ height: getContentHeight(), "--book-accent": accent }}>
      <div className="book-syntax__band">
        {[unitLabel, book.title, "Sentence Check"].filter(Boolean).join(" · ")}
      </div>
      <div className="book-syntax__head">
        <h2>
          <em>Sentence</em> Check
        </h2>
        {book.picture_url ? <img className="book-syntax__picture" src={book.picture_url} alt="" /> : null}
      </div>
      <div className="book-syntax__section">
        <i>A</i>
        <span>Read and find. 표시된 말이 문장에서 무슨 역할인지 골라요.</span>
      </div>
      <div className="book-syntax__body" ref={bodyRef}>
        <SyntaxAnalysisPage setCurrentStep={setCurrentStep} isPreview={isPreview} isReview={isReview} showStepsBar={false} />
      </div>
    </div>
  );
};

export default BookSyntaxPage;
